import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  Popconfirm,
  message,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  useStageMasters,
  useCreateStageMaster,
  useUpdateStageMaster,
  useDeleteStageMaster,
} from '../../hooks/useQCWorkflow';
import { WorkOrderStageMaster } from '../../services/qc-workflow.service';

const { Title } = Typography;
const { TextArea } = Input;

const StageMasterPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStage, setEditingStage] = useState<WorkOrderStageMaster | null>(null);

  const { data: stages = [], isLoading, refetch } = useStageMasters(false);
  const createMutation = useCreateStageMaster();
  const updateMutation = useUpdateStageMaster();
  const deleteMutation = useDeleteStageMaster();

  const handleAdd = () => {
    setEditingStage(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (stage: WorkOrderStageMaster) => {
    setEditingStage(stage);
    form.setFieldsValue({
      ...stage,
      is_active: stage.is_active !== undefined ? stage.is_active : true,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      refetch();
    } catch (error) {
      console.error('Error deleting stage:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingStage) {
        await updateMutation.mutateAsync({
          id: editingStage.id,
          data: values,
        });
      } else {
        await createMutation.mutateAsync(values);
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingStage(null);
      refetch();
    } catch (error) {
      console.error('Error saving stage:', error);
    }
  };

  const columns: ColumnsType<WorkOrderStageMaster> = [
    {
      title: 'Sequence',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 100,
      sorter: (a, b) => a.sequence_number - b.sequence_number,
    },
    {
      title: 'Stage Code',
      dataIndex: 'stage_code',
      key: 'stage_code',
      width: 150,
    },
    {
      title: 'Stage Name',
      dataIndex: 'stage_name',
      key: 'stage_name',
    },
    {
      title: 'Description',
      dataIndex: 'stage_description',
      key: 'stage_description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this stage?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />
            <Title level={4} style={{ margin: 0 }}>
              Stage Master
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Stage
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={stages}
          rowKey="id"
          loading={isLoading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title={editingStage ? 'Edit Stage Master' : 'Add Stage Master'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingStage(null);
        }}
        width={600}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="stage_code"
            label="Stage Code"
            rules={[
              { required: true, message: 'Please enter stage code' },
              { max: 50, message: 'Stage code must be less than 50 characters' },
            ]}
          >
            <Input placeholder="e.g., STG-001" />
          </Form.Item>

          <Form.Item
            name="stage_name"
            label="Stage Name"
            rules={[
              { required: true, message: 'Please enter stage name' },
              { max: 255, message: 'Stage name must be less than 255 characters' },
            ]}
          >
            <Input placeholder="e.g., Material Preparation" />
          </Form.Item>

          <Form.Item
            name="stage_description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Enter stage description"
            />
          </Form.Item>

          <Form.Item
            name="sequence_number"
            label="Sequence Number"
            rules={[
              { required: true, message: 'Please enter sequence number' },
              { type: 'number', min: 1, message: 'Sequence must be at least 1' },
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="Enter sequence number"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StageMasterPage;
